import { useState, useEffect } from 'react';
import { Button } from '../../../components/ui/button';
import { Card, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Switch } from '../../../components/ui/switch';
import { Plus, Edit, Trash2, RefreshCw, Settings, Clock, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import * as api from '../../../services/api';

const BibleReadingsManager = () => {
  const [readings, setReadings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isFetchDialogOpen, setIsFetchDialogOpen] = useState(false);
  const [editingReading, setEditingReading] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [fetchWeek, setFetchWeek] = useState('');
  const [fetchYear, setFetchYear] = useState(new Date().getFullYear());
  
  // Auto-fetch settings state
  const [autoFetchSettings, setAutoFetchSettings] = useState({
    auto_fetch_enabled: false,
    fetch_interval_hours: 24,
    weeks_ahead: 3,
    last_fetch: null,
    last_fetch_result: null
  });
  const [savingSettings, setSavingSettings] = useState(false);
  const [fetchingNow, setFetchingNow] = useState(false);
  
  const [formData, setFormData] = useState({
    date: '',
    first_reading: { sv: '' },
    first_reading_reference: '',
    psalm: { sv: '' },
    psalm_reference: '',
    second_reading: { sv: '' },
    second_reading_reference: '',
    alleluia: { sv: '' },
    alleluia_reference: '',
    gospel: { sv: '' },
    gospel_reference: '',
    reflection: { sv: '' },
    source: 'manual'
  });

  useEffect(() => {
    fetchReadings();
    fetchAutoFetchSettings();
  }, []);

  const fetchAutoFetchSettings = async () => {
    try {
      const settings = await api.getBibleReadingsSettings();
      setAutoFetchSettings(settings);
    } catch (error) {
      console.error('Could not load auto-fetch settings:', error);
    }
  };

  const handleSaveAutoFetchSettings = async () => {
    try {
      setSavingSettings(true);
      await api.updateBibleReadingsSettings(
        autoFetchSettings.auto_fetch_enabled,
        autoFetchSettings.fetch_interval_hours,
        autoFetchSettings.weeks_ahead
      );
      toast.success('Inställningar sparade');
    } catch (error) {
      toast.error('Kunde inte spara inställningar');
    } finally {
      setSavingSettings(false);
    }
  };

  const handleFetchNow = async () => {
    try {
      setFetchingNow(true);
      const result = await api.fetchBibleReadingsNow(autoFetchSettings.weeks_ahead);
      toast.success(
        `Hämtade ${result.fetched_count} nya läsningar. ${result.skipped_count} fanns redan.` +
        (result.failed_count > 0 ? ` ${result.failed_count} misslyckades.` : ''),
        { duration: 5000 }
      );
      fetchReadings();
      fetchAutoFetchSettings(); // Refresh to get last_fetch time
    } catch (error) {
      toast.error('Kunde inte hämta läsningar');
    } finally {
      setFetchingNow(false);
    }
  };

  const fetchReadings = async () => {
    try {
      setLoading(true);
      const data = await api.getBibleReadings();
      setReadings(data.sort((a, b) => new Date(b.date) - new Date(a.date)));
    } catch (error) {
      toast.error('Kunde inte hämta läsningar');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchWeek = async () => {
    if (!fetchWeek || !fetchYear) {
      toast.error('Ange både veckonummer och år');
      return;
    }

    const week = parseInt(fetchWeek);
    if (week < 1 || week > 53) {
      toast.error('Veckonummer måste vara mellan 1-53');
      return;
    }

    try {
      setFetching(true);
      const response = await api.fetchWeekReadingsFromKatolskakyrkan(week, fetchYear);
      
      toast.success(
        `Hämtade ${response.fetched_count} läsningar. ` +
        (response.failed_count > 0 ? `${response.failed_count} misslyckades.` : ''),
        { duration: 5000 }
      );
      
      setIsFetchDialogOpen(false);
      fetchReadings();
    } catch (error) {
      toast.error('Kunde inte hämta veckoläsningar från katolskakyrkan.se');
    } finally {
      setFetching(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingReading) {
        await api.updateBibleReading(editingReading.id, formData);
        toast.success('Läsning uppdaterad');
      } else {
        await api.createBibleReading(formData);
        toast.success('Läsning skapad');
      }
      
      setIsDialogOpen(false);
      resetForm();
      fetchReadings();
    } catch (error) {
      toast.error('Kunde inte spara läsning');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Är du säker på att du vill ta bort denna läsning?')) return;
    
    try {
      await api.deleteBibleReading(id);
      toast.success('Läsning borttagen');
      fetchReadings();
    } catch (error) {
      toast.error('Kunde inte ta bort läsning');
    }
  };

  const handleEdit = (reading) => {
    setEditingReading(reading);
    setFormData({
      date: reading.date.split('T')[0],
      first_reading: reading.first_reading,
      first_reading_reference: reading.first_reading_reference || '',
      psalm: reading.psalm,
      psalm_reference: reading.psalm_reference || '',
      second_reading: reading.second_reading || { sv: '' },
      second_reading_reference: reading.second_reading_reference || '',
      alleluia: reading.alleluia || { sv: '' },
      alleluia_reference: reading.alleluia_reference || '',
      gospel: reading.gospel,
      gospel_reference: reading.gospel_reference || '',
      reflection: reading.reflection || { sv: '' },
      source: reading.source || 'manual'
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setEditingReading(null);
    setFormData({
      date: '',
      first_reading: { sv: '' },
      first_reading_reference: '',
      psalm: { sv: '' },
      psalm_reference: '',
      second_reading: { sv: '' },
      second_reading_reference: '',
      alleluia: { sv: '' },
      alleluia_reference: '',
      gospel: { sv: '' },
      gospel_reference: '',
      reflection: { sv: '' },
      source: 'manual'
    });
  };

  const getCurrentWeek = () => {
    const now = new Date();
    const start = new Date(now.getFullYear(), 0, 1);
    const diff = now - start;
    const oneWeek = 604800000;
    return Math.ceil((diff / oneWeek) + ((start.getDay() + 1) / 7));
  };

  if (loading) {
    return <div className="p-6">Laddar...</div>;
  }

  return (
    <div className="p-6" data-testid="bible-manager">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Bibelläsningar</h2>
          <p className="text-gray-600">Hantera dagliga bibelläsningar</p>
        </div>
        <div className="flex gap-3">
          <Button
            onClick={() => {
              setFetchWeek(getCurrentWeek().toString());
              setIsFetchDialogOpen(true);
            }}
            variant="outline"
            className="flex items-center gap-2"
            data-testid="fetch-week-button"
          >
            <RefreshCw className="w-4 h-4" />
            Hämta från katolskakyrkan.se
          </Button>
          <Button
            onClick={() => {
              resetForm();
              setIsDialogOpen(true);
            }}
            className="flex items-center gap-2"
            data-testid="add-reading-button"
          >
            <Plus className="w-4 h-4" />
            Lägg till läsning
          </Button>
        </div>
      </div>

      {/* Auto-fetch Settings Card */}
      <Card className="mb-6 border-blue-200 bg-blue-50/50">
        <CardContent className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <Settings className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Automatisk Uppdatering</h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Enable/Disable Toggle */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Aktivera auto-hämtning</label>
              <div className="flex items-center gap-3">
                <Switch
                  checked={autoFetchSettings.auto_fetch_enabled}
                  onCheckedChange={(checked) => 
                    setAutoFetchSettings(prev => ({ ...prev, auto_fetch_enabled: checked }))
                  }
                  data-testid="auto-fetch-toggle"
                />
                <span className={`text-sm ${autoFetchSettings.auto_fetch_enabled ? 'text-green-600 font-medium' : 'text-gray-500'}`}>
                  {autoFetchSettings.auto_fetch_enabled ? 'Aktiverad' : 'Inaktiverad'}
                </span>
              </div>
            </div>

            {/* Interval Hours */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <Clock className="w-4 h-4" />
                Uppdateringsintervall
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="168"
                  value={autoFetchSettings.fetch_interval_hours}
                  onChange={(e) => 
                    setAutoFetchSettings(prev => ({ 
                      ...prev, 
                      fetch_interval_hours: parseInt(e.target.value) || 24 
                    }))
                  }
                  className="w-20"
                  data-testid="fetch-interval-input"
                />
                <span className="text-sm text-gray-600">timmar</span>
              </div>
            </div>

            {/* Weeks Ahead */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                Veckor framåt
              </label>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  max="6"
                  value={autoFetchSettings.weeks_ahead}
                  onChange={(e) => 
                    setAutoFetchSettings(prev => ({ 
                      ...prev, 
                      weeks_ahead: parseInt(e.target.value) || 3 
                    }))
                  }
                  className="w-20"
                  data-testid="weeks-ahead-input"
                />
                <span className="text-sm text-gray-600">veckor</span>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Åtgärder</label>
              <div className="flex gap-2">
                <Button
                  onClick={handleSaveAutoFetchSettings}
                  disabled={savingSettings}
                  size="sm"
                  data-testid="save-settings-button"
                >
                  {savingSettings ? 'Sparar...' : 'Spara'}
                </Button>
                <Button
                  onClick={handleFetchNow}
                  disabled={fetchingNow}
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  data-testid="fetch-now-button"
                >
                  <RefreshCw className={`w-3 h-3 ${fetchingNow ? 'animate-spin' : ''}`} />
                  {fetchingNow ? 'Hämtar...' : 'Hämta nu'}
                </Button>
              </div>
            </div>
          </div>

          {/* Last Fetch Info */}
          {autoFetchSettings.last_fetch && (
            <div className="mt-4 pt-4 border-t border-blue-200">
              <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                <span>
                  <strong>Senast hämtad:</strong>{' '}
                  {new Date(autoFetchSettings.last_fetch).toLocaleString('sv-SE')}
                </span>
                {autoFetchSettings.last_fetch_result && (
                  <>
                    <span className="text-green-600">
                      ✓ {autoFetchSettings.last_fetch_result.fetched_count} nya
                    </span>
                    <span className="text-gray-500">
                      ⏭ {autoFetchSettings.last_fetch_result.skipped_count} hoppades över
                    </span>
                    {autoFetchSettings.last_fetch_result.failed_count > 0 && (
                      <span className="text-red-600">
                        ✗ {autoFetchSettings.last_fetch_result.failed_count} misslyckades
                      </span>
                    )}
                  </>
                )}
              </div>
              {autoFetchSettings.last_fetch_result?.failed_dates?.length > 0 && (
                <div className="mt-2 text-xs text-red-500">
                  Misslyckade datum: {autoFetchSettings.last_fetch_result.failed_dates.join(', ')}
                </div>
              )}
            </div>
          )}

          {/* Help text */}
          <div className="mt-4 text-xs text-gray-500 bg-white/50 p-2 rounded">
            <p><strong>Tips:</strong> Systemet hämtar automatiskt läsningar från katolskakyrkan.se. 
            Om källan är tillfälligt otillgänglig försöker systemet igen med 3 försök per dag. 
            Befintliga läsningar skrivs aldrig över.</p>
          </div>
        </CardContent>
      </Card>

      <Dialog open={isFetchDialogOpen} onOpenChange={setIsFetchDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Hämta veckoläsningar från katolskakyrkan.se</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Veckonummer (1-53)</label>
              <Input
                type="number"
                min="1"
                max="53"
                value={fetchWeek}
                onChange={(e) => setFetchWeek(e.target.value)}
                placeholder="t.ex. 3"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">År</label>
              <Input
                type="number"
                value={fetchYear}
                onChange={(e) => setFetchYear(parseInt(e.target.value))}
                placeholder="t.ex. 2026"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded p-3 text-sm text-blue-800">
              <p><strong>Info:</strong> Systemet hämtar alla 7 dagar för den valda veckan.</p>
              <p className="mt-1">Läsningar som redan finns hoppas över automatiskt.</p>
            </div>
            <div className="flex gap-2">
              <Button onClick={handleFetchWeek} disabled={fetching} className="flex-1">
                {fetching ? 'Hämtar...' : 'Hämta veckoläsningar'}
              </Button>
              <Button
                onClick={() => setIsFetchDialogOpen(false)}
                variant="outline"
                disabled={fetching}
              >
                Avbryt
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingReading ? 'Redigera läsning' : 'Lägg till läsning'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Datum</label>
              <Input
                type="date"
                required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Första läsningen (Svenska)</label>
              <Input
                placeholder="Referens (t.ex. 1 Sam 1:1-8)"
                value={formData.first_reading_reference}
                onChange={(e) => setFormData({ ...formData, first_reading_reference: e.target.value })}
                className="mb-2"
              />
              <Textarea
                required
                rows={4}
                value={formData.first_reading.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  first_reading: { ...formData.first_reading, sv: e.target.value }
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Psaltaren (Svenska)</label>
              <Input
                placeholder="Referens (t.ex. Ps 116:12-19)"
                value={formData.psalm_reference}
                onChange={(e) => setFormData({ ...formData, psalm_reference: e.target.value })}
                className="mb-2"
              />
              <Textarea
                required
                rows={4}
                value={formData.psalm.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  psalm: { ...formData.psalm, sv: e.target.value }
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Andra läsningen (valfritt)</label>
              <Input
                placeholder="Referens"
                value={formData.second_reading_reference}
                onChange={(e) => setFormData({ ...formData, second_reading_reference: e.target.value })}
                className="mb-2"
              />
              <Textarea
                rows={3}
                value={formData.second_reading.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  second_reading: { ...formData.second_reading, sv: e.target.value }
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Allelujavers (valfritt)</label>
              <Input
                placeholder="Referens (t.ex. Mark 1:15)"
                value={formData.alleluia_reference}
                onChange={(e) => setFormData({ ...formData, alleluia_reference: e.target.value })}
                className="mb-2"
              />
              <Textarea
                rows={2}
                value={formData.alleluia.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  alleluia: { ...formData.alleluia, sv: e.target.value }
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Evangelium (Svenska)</label>
              <Input
                placeholder="Referens (t.ex. Mark 1:14-20)"
                value={formData.gospel_reference}
                onChange={(e) => setFormData({ ...formData, gospel_reference: e.target.value })}
                className="mb-2"
              />
              <Textarea
                required
                rows={4}
                value={formData.gospel.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  gospel: { ...formData.gospel, sv: e.target.value }
                })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Reflektion (valfritt)</label>
              <Textarea
                rows={3}
                value={formData.reflection.sv}
                onChange={(e) => setFormData({
                  ...formData,
                  reflection: { ...formData.reflection, sv: e.target.value }
                })}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setIsDialogOpen(false);
                  resetForm();
                }}
              >
                Avbryt
              </Button>
              <Button type="submit">
                {editingReading ? 'Uppdatera' : 'Skapa'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <div className="space-y-3">
        {readings.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center text-gray-500">
              Inga läsningar ännu. Hämta från katolskakyrkan.se eller lägg till manuellt.
            </CardContent>
          </Card>
        ) : (
          readings.map((reading) => (
            <Card key={reading.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="text-lg font-semibold">
                        {new Date(reading.date).toLocaleDateString('sv-SE', {
                          weekday: 'long',
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                      </h3>
                      {reading.source === 'katolskakyrkan' && (
                        <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
                          Auto-hämtad
                        </span>
                      )}
                      {reading.source === 'manual' && (
                        <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                          Manuell
                        </span>
                      )}
                    </div>
                    <div className="space-y-1 text-sm text-gray-600">
                      <p><strong>Första läsningen:</strong> {reading.first_reading_reference || 'Ingen referens'}</p>
                      <p><strong>Psaltaren:</strong> {reading.psalm_reference || 'Ingen referens'}</p>
                      {reading.second_reading_reference && (
                        <p><strong>Andra läsningen:</strong> {reading.second_reading_reference}</p>
                      )}
                      <p><strong>Evangelium:</strong> {reading.gospel_reference || 'Ingen referens'}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(reading)}
                      data-testid={`edit-reading-${reading.id}`}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(reading.id)}
                      data-testid={`delete-reading-${reading.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default BibleReadingsManager;
